import {
  buildRrule,
  describeRrule,
  WEEKDAYS,
  type Frequency,
  type RecurrenceState,
  type Weekday,
} from '../../utils/Rrule.ts'

interface RecurrenceBuilderProps {
  value: RecurrenceState
  onChange: (next: RecurrenceState) => void
}

const FREQUENCY_OPTIONS: { value: Frequency; label: string }[] = [
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'MONTHLY_NTH', label: 'Monthly (day of week)' },
  { value: 'MONTHLY_DAY', label: 'Monthly (day of month)' },
]

const WEEKDAY_LABELS: Record<Weekday, string> = {
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
  SU: 'Sun',
}

const POSITION_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: 'First' },
  { value: 2, label: 'Second' },
  { value: 3, label: 'Third' },
  { value: 4, label: 'Fourth' },
  { value: -1, label: 'Last' },
]

const labelClassName = 'block text-sm font-medium text-gray-900'
const selectClassName =
  'mt-1 block w-full rounded-md border-0 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600'

// Live, human-readable summary of the current pattern. buildRrule can throw on
// half-built states (e.g. WEEKLY with no weekday yet), so fall back gracefully.
const describeRecurrence = (state: RecurrenceState): string | null => {
  if (state.frequency === 'WEEKLY' && state.weekdays.length === 0) {
    return null
  }
  try {
    return describeRrule(buildRrule(state))
  } catch {
    return null
  }
}

const RecurrenceBuilder = ({ value, onChange }: RecurrenceBuilderProps) => {
  const update = (patch: Partial<RecurrenceState>) => {
    onChange({ ...value, ...patch })
  }

  const toggleWeekday = (day: Weekday) => {
    const isSelected = value.weekdays.includes(day)
    const weekdays = isSelected
      ? value.weekdays.filter((d) => d !== day)
      : // Keep weekdays in canonical Mon→Sun order regardless of click order.
        WEEKDAYS.filter((d) => d === day || value.weekdays.includes(d))
    update({ weekdays })
  }

  const summary = describeRecurrence(value)

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor="recurrence-frequency" className={labelClassName}>
          Repeats
        </label>
        <select
          id="recurrence-frequency"
          value={value.frequency}
          onChange={(event) =>
            update({ frequency: event.target.value as Frequency })
          }
          className={selectClassName}>
          {FREQUENCY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {value.frequency === 'WEEKLY' && (
        <div>
          <span className={labelClassName}>On these days</span>
          <div className="mt-2 flex flex-wrap gap-2">
            {WEEKDAYS.map((day) => {
              const isSelected = value.weekdays.includes(day)
              return (
                <button
                  key={day}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => toggleWeekday(day)}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold ring-1 ring-inset transition-colors ${
                    isSelected
                      ? 'bg-indigo-600 text-white ring-indigo-600'
                      : 'bg-white text-gray-900 ring-gray-300 hover:bg-gray-50'
                  }`}>
                  {WEEKDAY_LABELS[day]}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {value.frequency === 'MONTHLY_NTH' && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="recurrence-position" className={labelClassName}>
              Week
            </label>
            <select
              id="recurrence-position"
              value={value.monthlyPosition}
              onChange={(event) =>
                update({ monthlyPosition: Number(event.target.value) })
              }
              className={selectClassName}>
              {POSITION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="recurrence-weekday" className={labelClassName}>
              Day
            </label>
            <select
              id="recurrence-weekday"
              value={value.monthlyWeekday}
              onChange={(event) =>
                update({ monthlyWeekday: event.target.value as Weekday })
              }
              className={selectClassName}>
              {WEEKDAYS.map((day) => (
                <option key={day} value={day}>
                  {WEEKDAY_LABELS[day]}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {value.frequency === 'MONTHLY_DAY' && (
        <div>
          <label htmlFor="recurrence-day-of-month" className={labelClassName}>
            Day of month
          </label>
          <input
            id="recurrence-day-of-month"
            type="number"
            min={1}
            max={31}
            value={value.monthlyDayOfMonth}
            onChange={(event) =>
              update({ monthlyDayOfMonth: Number(event.target.value) })
            }
            className={selectClassName}
          />
        </div>
      )}

      <p className="text-xs text-gray-500">
        {summary ? (
          <>
            Summary: <span className="font-medium text-gray-700">{summary}</span>
          </>
        ) : (
          'Pick at least one day to set the recurrence.'
        )}
      </p>
    </div>
  )
}

export default RecurrenceBuilder
